import { supabase } from "@/integrations/supabase/client";

export interface DirectConversionResult {
  success: boolean;
  error?: string;
  orderId?: string;
}

/**
 * Service to handle direct deal-to-order conversion for MSA organizations.
 * This bypasses the customer proposal acceptance flow when a PO is provided.
 */
export class DirectDealConversionService {
  /**
   * Convert a deal directly to an order (for MSA organizations with PO).
   * 
   * @param dealId - The ID of the deal to convert
   * @param customerPO - The customer's Purchase Order number
   * @returns Promise with success status and optional error message
   */
  static async convertDealToOrder(
    dealId: string, 
    customerPO: string
  ): Promise<DirectConversionResult> {
    console.log('🔄 [DirectConversion] Starting direct deal-to-order conversion', { dealId, customerPO });

    try {
      // 1. Get current user for audit trail
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('❌ [DirectConversion] Authentication error:', sessionError);
        return { success: false, error: 'Not authenticated' };
      }

      const userId = session.user.id;
      const userEmail = session.user.email;
      console.log('✅ [DirectConversion] User authenticated:', { userId, userEmail });

      // 2. Fetch deal details to get existing deal_data
      const { data: dealData, error: fetchError } = await supabase
        .from('crm_deals')
        .select('deal_data, organization_id, contact_id')
        .eq('id', dealId)
        .single();

      if (fetchError || !dealData) {
        console.error('❌ [DirectConversion] Error fetching deal:', fetchError);
        return { success: false, error: 'Deal not found' };
      }

      // 3. Update deal with conversion info and order reference
      const existingDealData = (dealData.deal_data as Record<string, unknown>) || {};
      const updatedDealData = {
        ...existingDealData,
        customer_po: customerPO,
        direct_conversion: true,
        converted_by: userId,
        converted_by_email: userEmail,
        converted_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('crm_deals')
        .update({
          deal_data: updatedDealData,
          order_reference: customerPO
        })
        .eq('id', dealId);

      if (updateError) {
        console.error('❌ [DirectConversion] Error updating deal_data:', updateError);
        return { success: false, error: `Failed to update deal: ${updateError.message}` };
      }

      console.log('✅ [DirectConversion] Deal data updated with conversion info');

      // 4. Call mark_deal_as_won RPC (this triggers order creation via database trigger)
      const { data: rpcResult, error: rpcError } = await supabase.rpc('mark_deal_as_won', {
        deal_id: dealId
      });

      if (rpcError) {
        console.error('❌ [DirectConversion] Error calling mark_deal_as_won:', rpcError);
        return { success: false, error: `Failed to mark deal as won: ${rpcError.message}` };
      }

      console.log('✅ [DirectConversion] Deal marked as won via RPC:', rpcResult);

      // 5. Find and update the created order with PO number
      // Wait briefly for the trigger to create the order
      await new Promise(resolve => setTimeout(resolve, 500));

      // Find the order by looking for orders linked to this deal's sale
      const { data: saleData, error: saleError } = await supabase
        .from('sales')
        .select('id')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (saleError) {
        console.warn('⚠️ [DirectConversion] Error finding sale:', saleError);
      }

      if (saleData?.id) {
        // Update orders linked to this sale
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .update({ order_reference: customerPO })
          .eq('sale_id', saleData.id)
          .select('id')
          .maybeSingle();

        if (orderError) {
          console.warn('⚠️ [DirectConversion] Error updating order reference:', orderError);
          // Don't fail - the deal is already converted
        } else {
          console.log('✅ [DirectConversion] Order updated with PO number:', orderData?.id);
        }

        return { 
          success: true, 
          orderId: orderData?.id 
        };
      }

      // Even if we couldn't find the order, the conversion succeeded
      console.log('✅ [DirectConversion] Conversion complete (order may be created asynchronously)');
      return { success: true };

    } catch (error) {
      console.error('💥 [DirectConversion] Unexpected error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }

  /**
   * Check if an organization has an MSA in place.
   * 
   * @param organizationId - The ID of the organization to check
   * @returns Promise with MSA status and reference
   */
  static async checkOrganizationMsa(organizationId: string): Promise<{
    hasMsa: boolean;
    msaReference?: string;
  }> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single();

    if (error || !data) {
      console.warn('⚠️ [DirectConversion] Error checking MSA status:', error);
      return { hasMsa: false };
    }

    // Cast to access new MSA fields (types may not be updated yet)
    const orgData = data as unknown as { has_msa?: boolean; msa_reference?: string };

    return {
      hasMsa: orgData.has_msa || false,
      msaReference: orgData.msa_reference || undefined
    };
  }
}
